import type { Rule, Trait } from "@/pfp/generator";

type LayerOption = { id: string; name: string; traits: Trait[] };

function Picker({
  layers,
  value,
  onChange,
}: {
  layers: LayerOption[];
  value: { layerId: string; traitId: string };
  onChange: (v: { layerId: string; traitId: string }) => void;
}) {
  const layer = layers.find((l) => l.id === value.layerId) ?? layers[0];
  return (
    <span className="inline-flex items-center gap-1">
      <select
        className="input h-7 max-w-[120px] truncate"
        value={value.layerId}
        onChange={(e) => {
          const l = layers.find((x) => x.id === e.target.value)!;
          onChange({ layerId: l.id, traitId: l.traits[0]?.id ?? "" });
        }}
      >
        {layers.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </select>
      <span className="text-muted-foreground">=</span>
      <select
        className="input h-7 max-w-[140px] truncate"
        value={value.traitId}
        onChange={(e) => onChange({ layerId: value.layerId, traitId: e.target.value })}
      >
        {(layer?.traits ?? []).map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
    </span>
  );
}

const KIND_BADGE: Record<Rule["kind"], { label: string; classes: string }> = {
  incompatible: { label: "Block combo", classes: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" },
  requires:     { label: "Force pair",  classes: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
  weightShift:  { label: "Tilt rarity", classes: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" },
};

export function RuleSentence({
  rule,
  layers,
  onChange,
  onRemove,
}: {
  rule: Rule;
  layers: LayerOption[];
  onChange: (patch: Partial<Rule>) => void;
  onRemove: () => void;
}) {
  const meta = KIND_BADGE[rule.kind];
  return (
    <div className="card flex flex-wrap items-center gap-2 text-xs">
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${meta.classes}`}>
        {meta.label}
      </span>
      <span className="text-muted-foreground">When</span>
      {rule.kind === "incompatible" && (
        <>
          <Picker layers={layers} value={rule.a} onChange={(v) => onChange({ a: v })} />
          <span className="text-muted-foreground">never appears with</span>
          <Picker layers={layers} value={rule.b} onChange={(v) => onChange({ b: v })} />
        </>
      )}
      {rule.kind === "requires" && (
        <>
          <Picker layers={layers} value={rule.if} onChange={(v) => onChange({ if: v })} />
          <span className="text-muted-foreground">also forces</span>
          <Picker layers={layers} value={rule.then} onChange={(v) => onChange({ then: v })} />
        </>
      )}
      {rule.kind === "weightShift" && (
        <>
          <Picker layers={layers} value={rule.when} onChange={(v) => onChange({ when: v })} />
          <span className="text-muted-foreground">scales rarity of</span>
          <Picker layers={layers} value={rule.on} onChange={(v) => onChange({ on: v })} />
          <span className="text-muted-foreground">by</span>
          <input
            type="number"
            step="0.5"
            className="input h-7 w-16"
            value={rule.multiplier}
            onChange={(e) => onChange({ multiplier: Number(e.target.value) })}
          />
          <span className="text-muted-foreground">×</span>
        </>
      )}
      <button className="btn-ghost ml-auto h-6 px-2 text-xs" onClick={onRemove} aria-label="Remove rule">
        ×
      </button>
    </div>
  );
}
